import { apiClient, API_PRX } from '@indipia/shared';

// ─────────────────────────────────────────────────────────────────────────────
// 자재거래처→시공파트너 원장 통합: sv-member 의 /member/vendors 는 폐지됐고, 자재파트너는
// /member/partners(partnerRole=MATERIAL) 로 통합 조회·등록·수정·이력·평가된다. 이 모듈은
// 기존 화면(VendorItem 형태)을 그대로 유지하기 위해 /member/partners 응답(PartnerResponse)을
// VendorItem 으로, 거래처 저장 payload 를 파트너 payload 로 변환해 중계한다.
// (planner 의 /schedule/vendors* 발주 경로는 그대로 유지한다.)
// ─────────────────────────────────────────────────────────────────────────────

/** 배송지역 시군구 (코드 + 표시명) */
export interface VendorSigungu {
  sigunguCode: string;
  sigunguName: string;
}

/** sv-member /member/partners 응답 중 자재파트너 매핑에 쓰는 필드 (PartnerResponse) */
interface PartnerRow {
  partnerId: number;
  partnerName: string;
  mobile: string;
  email: string | null;
  partnerRole: string | null;
  contactName: string | null;
  partnerAddress: string | null;
  dealItem: string | null;
  dealStatus: string | null;
  commYn?: string;
  subWorkTypes: string[] | null;
  sigungus: Array<{ code: string; name: string }> | null;
  avgRating: number | null;
  reviewCount: number;
  partnerBizNo: string | null;
  partnerBizName: string | null;
  partnerRepName: string | null;
  memo: string | null;
  createdById: number;
  createdByName: string;
  createdAt: string;
  lastConstructionSiteName: string | null;
  lastConstructionDate: string | null;
}

/** 통합 원장(파트너) 1행을 기존 화면이 쓰는 VendorItem 형태로 변환 */
function partnerRowToVendor(p: PartnerRow): VendorItem {
  return {
    vendorId: p.partnerId,
    vendorTypeCode: p.partnerRole ?? 'MATERIAL',
    vendorName: p.partnerBizName ?? p.partnerName,
    vendorBizNo: p.partnerBizNo,
    vendorRepName: p.partnerRepName,
    contactName: p.contactName,
    vendorPhone: p.mobile,
    vendorEmail: p.email,
    dealStatus: p.dealStatus,
    commYn: p.commYn,
    vendorAddress: p.partnerAddress,
    workTypes: p.subWorkTypes ?? [],
    dealItem: p.dealItem,
    sigungus: (p.sigungus ?? []).map((s) => ({ sigunguCode: s.code, sigunguName: s.name })),
    avgRating: p.avgRating,
    reviewCount: p.reviewCount,
    vendorMemo: p.memo,
    createdById: p.createdById,
    createdByName: p.createdByName,
    createdAt: p.createdAt,
    lastOrderSiteName: p.lastConstructionSiteName,
    lastOrderDate: p.lastConstructionDate,
  };
}

/** 거래처 저장/수정 입력(VendorItem 계열)을 /member/partners payload 로 변환 */
function vendorPayloadToPartner(payload: {
  vendorName?: string;
  vendorBizNo?: string | null;
  vendorRepName?: string | null;
  contactName?: string | null;
  vendorPhone?: string;
  vendorEmail?: string | null;
  vendorAddress?: string | null;
  dealStatus?: string | null;
  commYn?: string | null;
  workTypes?: string[];
  dealItem?: string | null;
  sigungus?: VendorSigungu[];
  vendorMemo?: string | null;
}): Record<string, unknown> {
  return {
    partnerName: payload.vendorName,
    mobile: payload.vendorPhone,
    email: payload.vendorEmail,
    contactName: payload.contactName,
    partnerAddress: payload.vendorAddress,
    dealItem: payload.dealItem,
    dealStatus: payload.dealStatus,
    commYn: payload.commYn,
    subWorkTypes: payload.workTypes,
    sigungus: payload.sigungus?.map((s) => ({ code: s.sigunguCode, name: s.sigunguName })),
    partnerBizNo: payload.vendorBizNo,
    partnerBizName: payload.vendorName,
    partnerRepName: payload.vendorRepName,
    memo: payload.vendorMemo,
  };
}

export interface VendorItem {
  vendorId: number;
  vendorTypeCode: string;
  vendorName: string;
  vendorBizNo: string | null;
  vendorRepName: string | null;
  contactName: string | null;
  vendorPhone: string;
  vendorEmail: string | null;
  /** 거래 상태 (공통코드 E01_PARTNER_DEAL_STATUS: ACTIVE/SUSPENDED) */
  dealStatus: string | null;
  /** 연락 방식 ('Y'=알림톡 발송-회신 / 'N'=결과만 직접등록) */
  commYn?: string;
  vendorAddress: string | null;
  /** 거래품목(공종 코드 다중) */
  workTypes: string[];
  /** 세부취급품목(자유텍스트) */
  dealItem: string | null;
  /** 배송지역 시군구 목록 */
  sigungus: VendorSigungu[];
  /** 평균별점 (평가 없으면 null·미제공) */
  avgRating?: number | null;
  /** 평가 건수 (미제공 가능) */
  reviewCount?: number;
  /** 거래(발주) 건수 (미제공 가능) */
  orderCount?: number;
  vendorMemo: string | null;
  createdById: number;
  createdByName: string;
  createdAt: string;
  lastOrderSiteName: string | null;
  lastOrderDate: string | null;
}

export async function fetchVendors(keyword?: string): Promise<VendorItem[]> {
  const res = await apiClient.get(`${API_PRX.MEMBER}/partners`, {
    params: { role: 'MATERIAL', ...(keyword ? { keyword } : {}) },
  });
  const rows = (res.data.data ?? []) as PartnerRow[];
  return rows.map(partnerRowToVendor);
}

/** 중복 확인 — 사업자번호(vendorBizNo) 기준. 자재파트너는 항상 사업자 */
export async function checkVendorExists(
  vendorBizNo: string,
  excludeVendorId?: number,
): Promise<boolean> {
  const res = await apiClient.get(`${API_PRX.MEMBER}/partners/exists`, {
    params: { partnerBizNo: vendorBizNo, ...(excludeVendorId ? { excludePartnerId: excludeVendorId } : {}) },
  });
  return (res.data.data?.exists ?? false) as boolean;
}

/**
 * 현장 카드 안에 나열되는 개별 발주 건(작업건). 평가는 현장+공종(작업건) 단위이므로
 * 이 건의 공종(workType)에 걸린 평가(rating/evalMemo)를 함께 내려준다(평가 없으면 둘 다 null).
 */
export interface TransactionHistoryItem {
  orderId: number;
  workType: string | null;
  dealItem: string | null;
  orderedDate: string | null;
  expectedDate: string | null;
  rating: number | null;
  evalMemo: string | null;
  /** 현장이 완료면 true — 평가 입력·수정·삭제 모두 잠김. 서버가 내려주지 않으면(undefined) 잠그지 않음 */
  reviewLocked?: boolean;
}

/** M1 응답의 현장 단위 그룹 — items(작업건)마다 평가가 걸린다(현장+공종 단위) */
export interface SiteTransactionHistory {
  siteId: number;
  siteName: string | null;
  customerName: string | null;
  items: TransactionHistoryItem[];
}

export interface TransactionHistoryResponse {
  sites: SiteTransactionHistory[];
  totalCount: number;
  hasNext: boolean;
}

/** M1 — 거래처 거래이력(현장별 그룹, 현장 종합평가 포함) 조회 */
export async function fetchVendorTransactionHistory(
  vendorId: number,
  page = 0,
  size = 20,
): Promise<TransactionHistoryResponse> {
  const res = await apiClient.get(`${API_PRX.MEMBER}/partners/${vendorId}/transaction-history`, {
    params: { page, size },
  });
  return res.data.data;
}

/** M2 — 현장+공종(작업건) 단위 평가 등록/수정(upsert). workTypeCode가 키의 일부(필수) */
export async function upsertSiteReview(
  vendorId: number,
  siteId: number,
  payload: { workTypeCode: string; rating?: number | null; evalMemo?: string | null },
): Promise<void> {
  await apiClient.put(`${API_PRX.MEMBER}/partners/${vendorId}/sites/${siteId}/review`, payload);
}

export async function createVendor(payload: {
  vendorTypeCode: string;
  vendorName: string;
  contactName?: string | null;
  vendorPhone: string;
  vendorBizNo?: string | null;
  vendorRepName?: string | null;
  vendorEmail?: string | null;
  dealStatus?: string | null;
  workTypes?: string[];
  dealItem?: string | null;
  sigungus?: VendorSigungu[];
  vendorMemo?: string | null;
}): Promise<number> {
  const res = await apiClient.post(`${API_PRX.MEMBER}/partners`, {
    ...vendorPayloadToPartner(payload),
    partnerRole: 'MATERIAL',
    partnerBizYn: 'Y',
  });
  return res.data.data.partner_id as number;
}

/**
 * 이 자재거래처가 주거래처로 등록된 공종코드 목록 조회 (sv-schedule)
 * GET /schedule/order-settings/for-vendor?vendorId
 */
export async function fetchOrderSettingVendorWorkTypes(vendorId: number): Promise<string[]> {
  const res = await apiClient.get(`/schedule/order-settings/for-vendor`, {
    params: { vendorId },
  });
  return (res.data.data ?? []) as string[];
}

export async function updateVendor(
  vendorId: number,
  payload: {
    action: 'update' | 'delete';
    vendorName?: string;
    contactName?: string | null;
    vendorPhone?: string;
    vendorBizNo?: string | null;
    vendorRepName?: string | null;
    vendorEmail?: string | null;
    dealStatus?: string | null;
    commYn?: string | null;
    workTypes?: string[];
    dealItem?: string | null;
    sigungus?: VendorSigungu[];
    vendorMemo?: string | null;
  },
): Promise<void> {
  // partnerRole 은 보내지 않는다(null=미변경) — 자재/시공 겸용(BOTH) 파트너의 역할이 바뀌지 않도록.
  await apiClient.patch(`${API_PRX.MEMBER}/partners/${vendorId}`, {
    action: payload.action,
    ...vendorPayloadToPartner(payload),
  });
}

// ── Planner 거래이력·지표 (sv-schedule, 헤더 X-Comp-Id는 apiClient 규약) ─────────────

export interface VendorOrderRequestView {
  sentDtm: string;
  /** 'ACCEPTED' | 'DECLINED' | null(회신 전) */
  replyResultCode: string | null;
  replyDtm: string | null;
}

export interface VendorShipRequestView {
  sentDtm: string;
  replyDtm: string | null;
}

export interface VendorReceiptView {
  receiptDate: string;
  /** 입고 시점 입고희망일 스냅샷 */
  promisedDate: string | null;
}

/** 거래처 원장 행(orderVendorId = 거래이력 item.orderId)별 발주·양중·배송·입고 요약 */
export interface VendorOrderHistoryItem {
  orderVendorId: number;
  taskId: number;
  orderStatusCode: string;
  deliveryDate: string | null;
  orderRequest: VendorOrderRequestView | null;
  liftReqYn: 'Y' | 'N';
  liftAvailYn: 'Y' | 'N' | null;
  shipRequest: VendorShipRequestView | null;
  receipt: VendorReceiptView | null;
}

/** 응답 data 가 배열이 아니면(null/undefined 포함) 응답 형식 오류로 던진다 — 빈 배열은 정상 값 */
function requireArray<T>(data: unknown): T[] {
  if (!Array.isArray(data)) throw new Error('INVALID_RESPONSE_SHAPE');
  return data as T[];
}

/** 거래처 거래이력 요약 조회 — 이력 없음·타 사업장은 빈 배열 (sv-schedule GET /schedule/vendors/{vendorId}/order-history) */
export async function fetchVendorOrderHistory(vendorId: number): Promise<VendorOrderHistoryItem[]> {
  const res = await apiClient.get(`/schedule/vendors/${vendorId}/order-history`);
  return requireArray<VendorOrderHistoryItem>(res.data?.data);
}

/** 자재파트너 지표 — 분모 0인 %는 null(생략). 행은 정상 발주 요청이 나간 거래처만 존재 */
export interface VendorMetrics {
  vendorId: number;
  tradeCount: number;
  lastTradeDate: string | null;
  replyWithinTwoHoursPercent: number | null;
  onTimeDeliveryPercent: number | null;
  liftAvailPercent: number | null;
  /** 서버 추가 필드(지시서 §3.4 외, 배송시간 준수). 이 단위에서는 표시하지 않음 */
  deliveryTimelyPercent: number | null;
}

/** 자재파트너 지표 목록 조회 — 목록(fetchVendors)과 vendorId로 병합 (sv-schedule GET /schedule/vendors/metrics) */
export async function fetchVendorMetrics(): Promise<VendorMetrics[]> {
  const res = await apiClient.get(`/schedule/vendors/metrics`);
  return requireArray<VendorMetrics>(res.data?.data);
}
